import type { Board } from "../../board";
import type { DamageType } from "../../damageType";
import type { ProjectileHittable, PostDeserializable } from "../../interfaces";
import { PhysicalThingWithHp } from "../../physicalThingWithHp";
import { deserializeXY, serializeXY } from "../../serialization";
import type { Thing } from "../../thing";
import { XY } from "../../xy";
import { ZOrder } from "../../zOrder";
import { Portal2 } from "./portal2";

export class Portal2Part extends PhysicalThingWithHp implements ProjectileHittable, PostDeserializable {
	static key = "Portal2Part";
	private master!: Portal2;
	private _pendingMasterXY: { x: number; y: number } | null = null;

	constructor(xy: XY) {
		super(xy, true);
	}

	setMaster(master: Portal2): void {
		this.master = master;
		// Share damage multipliers with master
	}

	getMaster(): Portal2 {
		return this.master;
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		// If damage comes from our master Portal2 (e.g. during onZeroHp kill chain), apply to self
		if (source instanceof Portal2) {
			return super.applyDamage(type, amount, source);
		}
		// Otherwise proxy damage to master
		if (this.master) {
			return this.master.applyDamage(type, amount, source);
		}
		return super.applyDamage(type, amount, source);
	}

	onProjectileHit(projectile: unknown): void {
		if (this.master) {
			this.master.onProjectileHit(projectile);
		}
	}

	isHittable(): boolean {
		return true;
	}

	isPassable(): boolean {
		return false;
	}

	override isVisible(): boolean {
		return this.master ? this.master.isVisible() : true;
	}

	override getZOrder(): number {
		return this.master ? this.master.getZOrder() : ZOrder.PORTAL;
	}

	override isHideUnderneath(): boolean {
		return this.master ? this.master.isHideUnderneath() : true;
	}

	postDeserialize(board: Board): void {
		if (!this._pendingMasterXY) return;
		const masterXY = new XY(this._pendingMasterXY.x, this._pendingMasterXY.y);
		const things = board.getThingsAt(masterXY);
		const master = things.find((t) => t instanceof Portal2);
		if (master) {
			this.setMaster(master as Portal2);
			(master as Portal2).addPart(this);
		}
		this._pendingMasterXY = null;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			masterXY: this.master ? serializeXY(this.master.xy) : null,
		};
	}

	static deserialize(data: any): Portal2Part {
		const part = new Portal2Part(deserializeXY(data.xy));
		part.restoreBaseFields(data);
		if (data.masterXY) {
			part._pendingMasterXY = data.masterXY;
		}
		return part;
	}
}
